import { createBrowserRouter } from "react-router";

import { ProtectedRoute } from "~/auth/protected-route";
import { AppShell } from "~/components/app-shell";
import AdminHomeRoute from "~/routes/admin/index";
import AdminWardMembersRoute from "~/routes/admin/ward-members";
import AdminWardsRoute from "~/routes/admin/wards";
import HomeRoute from "~/routes/home";
import LoginRoute from "~/routes/login";
import NotFoundRoute from "~/routes/not-found";
import ProfileRoute from "~/routes/profile";
import RegisterRoute from "~/routes/register";
import SelectWardRoute from "~/routes/select-ward";
import SessionAuditRoute from "~/routes/session/audit";
import OvernightUpdateRoute from "~/routes/session/overnight-update";
import PatientDetailRoute from "~/routes/session/patient-detail";
import EditPatientRoute from "~/routes/session/patient-edit";
import NewPatientRoute from "~/routes/session/patient-new";
import SessionPatientsRoute from "~/routes/session/patients";
import StartHandoverRoute from "~/routes/session/start-handover";
import SessionLayout from "~/routes/session-layout";
import SettingsRoute from "~/routes/settings";

// Exported separately so tests can mount the same config in a memory router.
export const routes = [
  // Public routes render bare — outside <ProtectedRoute> and <AppShell> — so
  // they appear immediately without waiting on Firebase.
  {
    path: "/login",
    element: <LoginRoute />,
  },
  {
    path: "/register",
    element: <RegisterRoute />,
  },
  // Everything else is gated: <ProtectedRoute> bounces anonymous visitors to
  // /login, then <AppShell> wraps the matched child route via <Outlet>.
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      // The logged-in landing is the code-entry home (E3.1): the incoming doctor
      // enters a handover code, the outgoing doctor links out to /select-ward.
      {
        index: true,
        element: <HomeRoute />,
      },
      {
        path: "select-ward",
        element: <SelectWardRoute />,
      },
      // Per-user device settings (V2-E4.1): the notification permission +
      // subscribe flow. Available to every authenticated user.
      {
        path: "settings",
        element: <SettingsRoute />,
      },
      // Account profile (V2-E6.3): the phone number for SMS handover-code
      // delivery. Available to every authenticated user.
      {
        path: "profile",
        element: <ProfileRoute />,
      },
      // Admin area (V2). The landing lists the sections the user can reach; each
      // screen also guards itself (ward CRUD is super-admin only — E1.2).
      {
        path: "admin",
        element: <AdminHomeRoute />,
      },
      {
        path: "admin/wards",
        element: <AdminWardsRoute />,
      },
      {
        path: "admin/wards/:wardId/members",
        element: <AdminWardMembersRoute />,
      },
      // Session-scoped routes nest under <SessionLayout>, which loads the
      // session once and provides it (and its ward) to every child via context.
      // The API's 404 is authoritative — a session on a ward the caller can't
      // access renders the not-found page. Future epics (E4–E6) fill in the
      // child screens below.
      {
        path: "session/:id",
        element: <SessionLayout />,
        children: [
          { index: true, element: <SessionPatientsRoute /> },
          { path: "patient/new", element: <NewPatientRoute /> },
          { path: "patient/:patientId", element: <PatientDetailRoute /> },
          { path: "patient/:patientId/edit", element: <EditPatientRoute /> },
          {
            path: "patient/:patientId/overnight-update",
            element: <OvernightUpdateRoute />,
          },
          { path: "start-handover", element: <StartHandoverRoute /> },
          { path: "audit", element: <SessionAuditRoute /> },
        ],
      },
      // Anything else, for a logged-in user, is a 404 inside the shell.
      {
        path: "*",
        element: <NotFoundRoute />,
      },
    ],
  },
] satisfies Parameters<typeof createBrowserRouter>[0];

export const router = createBrowserRouter(routes);
